import React, { useState, useRef, useCallback } from 'react';
import { getImageUrl, getOptimizedImageUrl } from '../utils/imageUtils';

interface SafeImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src?: string;
  maxRetries?: number;
  priority?: 'high' | 'low' | 'auto';
  fallback?: React.ReactNode;
  imgClassName?: string;
    optimizedWidth?: number;
    // quality render hero (speed 2026-10-22): LP hero 640/q70 sinkron dgn
    // preload head (public.html). Default 80 = perilaku lama (form/dll).
    optimizedQuality?: number;
  // cacheBust=false → URL gambar stabil (cacheable). Untuk gambar produk LP (LCP).
  cacheBust?: boolean;
}

const SafeImage: React.FC<SafeImageProps> = ({
  src,
  maxRetries = 2,
  priority = 'auto',
  loading: loadingProp,
  onError,
  alt = '',
  fallback,
  onLoad: externalOnLoad,
  className: externalClassName,
  imgClassName = 'object-contain',
    optimizedWidth,
    optimizedQuality = 80,
  cacheBust = true,
  ...rest
}) => {
  const loading = priority === 'high' ? 'eager' : (loadingProp ?? 'lazy');
  const fetchpriority = priority === 'auto' ? undefined : priority;
  const decoding = priority === 'high' ? 'async' : undefined;
  const retryCountRef = useRef(0);
  const [srcKey, setSrcKey] = useState(0);
  const [failed, setFailed] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const handleError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    if (retryCountRef.current < maxRetries) {
      retryCountRef.current += 1;
      setSrcKey(c => c + 1);
    } else {
      setFailed(true);
      onError?.(e);
    }
  }, [maxRetries, onError]);

  if (!src) return null;
  if (failed) {
    return fallback ? <>{fallback}</> : null;
  }

  const optimizedUrl = getOptimizedImageUrl(src, optimizedWidth ? { width: optimizedWidth, quality: optimizedQuality, format: 'webp' } : { quality: optimizedQuality, format: 'webp' }) ?? src ?? '';
  const cacheBustedUrl = getImageUrl(optimizedUrl, cacheBust ? undefined : { bust: false });
  // S4: srcset 400/800 utk gambar NON-LCP (hemat ~2x byte di HP). LCP (high)
  // SENGAJA single-URL agar cocok dgn head-preload (S1) — srcset bikin browser
  // pilih kandidat yg tak bisa diprediksi preload → double-fetch.
  const srcSet = priority !== 'high' && !cacheBust && (src?.includes('supabase.co/storage') ?? false)
    ? [400, 800].filter(w => w <= (optimizedWidth ?? 800)).map(w => `${getOptimizedImageUrl(src, { width: w, quality: 80, format: 'webp' })} ${w}w`).join(', ')
    : undefined;
  const finalUrl = srcKey > 0
    ? `${cacheBustedUrl}${cacheBustedUrl.includes('?') ? '&' : '?'}retry=${Date.now()}_${srcKey}`
    : cacheBustedUrl;

  // Gambar LCP/above-the-fold (priority=high) TIDAK boleh digate oleh state JS
  // "onLoad": browser sudah bisa paint begitu selesai decode, tapi kalau kita
  // set opacity-0 sampai onLoad + setState + re-render, paint tertunda beberapa
  // ratus ms (parah di device lambat) → LCP naik. Jadi untuk high: visible
  // langsung tanpa skeleton overlay/fade.
  // HALAMAN STATIS (SSR LP/form): gambar non-LCP dirender dgn opacity-0 +
  // skeleton sampai onLoad JS. Di HTML statis JS belum jalan → thumbnail /
  // preview di bawah fold tampak KOTAK PUTIH sampai di-scroll (insiden
  // 2026-10-25 "3 foto blank, digeser baru muncul"). Di halaman statis
  // gambar HARUS langsung terlihat tanpa bergantung state JS.
  const isStaticPage = typeof window === 'undefined'
    || !!(window as any).__LP_STATIC__
    || !!(window as any).__FORM_STATIC__;
  const eagerPaint = priority === 'high' || isStaticPage;

  return (
    <div className={`relative max-w-full ${externalClassName || ''}`}>
      {!imgLoaded && !eagerPaint && (
        <div className="absolute inset-0 bg-slate-200 dark:bg-slate-700 animate-pulse rounded" />
      )}
      <img
        src={finalUrl}
        alt={alt}
        loading={loading}
        fetchPriority={fetchpriority}
        decoding={decoding}
        srcSet={srcSet}
        sizes={srcSet ? '100vw' : undefined}
        onLoad={(e) => {
          setImgLoaded(true);
          externalOnLoad?.(e);
        }}
        onError={handleError}
        className={`w-full h-full ${imgClassName} ${eagerPaint || imgLoaded ? 'opacity-100' : 'opacity-0'} ${eagerPaint ? '' : 'transition-opacity duration-300'}`}
        {...rest}
      />
    </div>
  );
};

export default SafeImage;
